import { Component, computed, effect, ElementRef, input, signal, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { EMicaChat_EstadoMensajeMedia, MicaChatAudioData } from '@core/enums/micachat.enum';

@Component({
  selector: 'app-mica-chat-mensaje-audio',
  imports: [],
  templateUrl: './mica-chat-mensaje-audio.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './mica-chat-mensaje-audio.css',
})
export class MicaChatMensajeAudio {
  audio = input.required<MicaChatAudioData | null>();

  private readonly audioElement = viewChild<ElementRef<HTMLAudioElement>>('audioRef');

  currentTime = signal(0);
  duration = signal(0);
  isPlaying = signal(false);

  readonly isReady = computed(
    () => this.audio()?.status === EMicaChat_EstadoMensajeMedia.DESCARGADO,
  );
  readonly streamUrl = computed(() => this.audio()?.stream_url?.trim() ?? '');
  // Si quieres que el ancho represente qué tan largo es el audio (estilo WhatsApp)
  readonly playerWidth = computed(() => {
    const minW = 160; // px
    const maxW = 350; // px
    const secondsFactor = 5; // aumenta 5px por cada segundo de audio

    const width = minW + this.duration() * secondsFactor;
    return `${Math.min(width, maxW)}px`;
  });

  constructor() {
    effect(() => {
      const canPlay = this.isReady() && this.streamUrl().length > 0;
      if (canPlay) return;

      this.pausePlayback();
      this.currentTime.set(0);
      this.duration.set(0);
    }, { allowSignalWrites: true });

    effect(() => {
      const streamUrl = this.streamUrl();
      if (!streamUrl) return;

      this.pausePlayback();
      this.currentTime.set(0);
      this.duration.set(0);

      queueMicrotask(() => {
        const audio = this.audioElement()?.nativeElement;
        audio?.load();
      });
    }, { allowSignalWrites: true });
  }

  togglePlayPause(): void {
    if (!this.isReady()) return;
    if (!this.streamUrl()) return;

    const audio = this.audioElement()?.nativeElement;
    if (!audio) return;

    if (audio.paused) {
      audio
        .play()
        .then(() => {
          this.isPlaying.set(true);
        })
        .catch((error) => {
          console.error('No se pudo reproducir el audio:', error);
          this.isPlaying.set(false);
        });
      return;
    }

    this.pausePlayback();
  }

  onLoadedMetadata(event: Event): void {
    const target = event.target as HTMLAudioElement;
    if (!Number.isFinite(target.duration)) return;
    this.duration.set(target.duration);
  }

  onTimeUpdate(event: Event): void {
    const target = event.target as HTMLAudioElement;
    this.currentTime.set(target.currentTime ?? 0);
  }

  onEnded(): void {
    this.isPlaying.set(false);
    this.currentTime.set(0);
  }

  onSeek(event: Event): void {
    const target = event.target as HTMLInputElement;
    const nextTime = Number(target.value);

    if (!Number.isFinite(nextTime)) return;

    const audio = this.audioElement()?.nativeElement;
    if (!audio) return;

    audio.currentTime = nextTime;
    this.currentTime.set(nextTime);
  }

  formatTime(seconds: number): string {
    if (!Number.isFinite(seconds) || seconds < 0) {
      return '0:00';
    }

    const roundedSeconds = Math.floor(seconds);
    const mins = Math.floor(roundedSeconds / 60);
    const secs = roundedSeconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  private pausePlayback(): void {
    const audio = this.audioElement()?.nativeElement;
    if (!audio) return;

    audio.pause();
    this.isPlaying.set(false);
  }
}
